import type { O2dTab } from '../interfaces/tab.interface.js';

/**
 * Pure enabled-tab navigation helpers for `<o2d-tab-list>` keyboard/roving
 * behaviour. Each helper operates over the tab config and returns a tab id (or
 * `undefined` when no enabled tab exists), skipping disabled tabs and — for the
 * relative movers — wrapping around the ends. No DOM, no side effects, so they
 * are unit-testable in isolation.
 */

/**
 * First enabled tab id (leading disabled tabs are skipped), or `undefined` when
 * the config is empty or every tab is disabled.
 */
export function firstEnabled(tabs: readonly O2dTab[]): string | undefined {
    return tabs.find((tab) => !tab.disabled)?.id;
}

/**
 * Last enabled tab id (trailing disabled tabs are skipped), or `undefined` when
 * the config is empty or every tab is disabled.
 */
export function lastEnabled(tabs: readonly O2dTab[]): string | undefined {
    return findLastEnabled(tabs)?.id;
}

/**
 * Next enabled tab id after `currentId`, wrapping past the end and skipping
 * disabled tabs. Returns `currentId` when it is the only enabled tab, falls back
 * to `firstEnabled` for an unknown `currentId`, and `undefined` when no tab is
 * enabled.
 */
export function nextEnabled(tabs: readonly O2dTab[], currentId: string): string | undefined {
    return stepEnabled(tabs, currentId, 1, firstEnabled);
}

/**
 * Previous enabled tab id before `currentId`, wrapping past the start and
 * skipping disabled tabs. Returns `currentId` when it is the only enabled tab,
 * falls back to `lastEnabled` for an unknown `currentId`, and `undefined` when
 * no tab is enabled.
 */
export function prevEnabled(tabs: readonly O2dTab[], currentId: string): string | undefined {
    return stepEnabled(tabs, currentId, -1, lastEnabled);
}

/**
 * Walks `tabs` in `direction` (+1 next, -1 prev) starting from the tab after
 * `currentId`, wrapping around, and returns the first enabled id encountered.
 * Unknown `currentId` delegates to `fallback` (first/last enabled).
 */
function stepEnabled(
    tabs: readonly O2dTab[],
    currentId: string,
    direction: 1 | -1,
    fallback: (tabs: readonly O2dTab[]) => string | undefined,
): string | undefined {
    const count = tabs.length;
    const start = tabs.findIndex((tab) => tab.id === currentId);
    if (start === -1) {
        return fallback(tabs);
    }
    for (let step = 1; step <= count; step++) {
        const index = (((start + direction * step) % count) + count) % count;
        const candidate = tabs[index];
        if (candidate && !candidate.disabled) {
            return candidate.id;
        }
    }
    return undefined;
}

/**
 * Last non-disabled tab, or `undefined`. Iterates from the end so the search
 * short-circuits on the trailing enabled tab.
 */
function findLastEnabled(tabs: readonly O2dTab[]): O2dTab | undefined {
    for (let index = tabs.length - 1; index >= 0; index--) {
        const tab = tabs[index];
        if (tab && !tab.disabled) {
            return tab;
        }
    }
    return undefined;
}
